import { useState, useEffect, type ReactElement, type ReactNode, type SyntheticEvent } from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import type { TabsProps } from '@mui/material/Tabs';
import type { TabProps } from '@mui/material/Tab';
import { Link as RouterLink, useLocation, useSearchParams } from 'react-router-dom';

function mergeSx(...items: Array<SxProps<Theme> | undefined>): SxProps<Theme> {
  const collected: SxProps<Theme>[] = [];
  items.forEach((item) => {
    if (!item) return;
    if (Array.isArray(item)) {
      collected.push(...item);
    } else {
      collected.push(item);
    }
  });

  if (collected.length === 0) {
    return {};
  }
  if (collected.length === 1) {
    return collected[0];
  }
  return collected as SxProps<Theme>;
}

export interface ButtonTab {
  id: string;
  label: ReactNode;
  icon?: ReactElement;
  disabled?: boolean;
  tabProps?: Omit<TabProps, 'value' | 'label' | 'component'>;
  content?: ReactNode | (() => ReactNode);
}

interface ButtonTabsProps {
  tabs: ButtonTab[];
  defaultValue?: string;
  defaultTab?: string;
  value?: string;
  onChange?: (event: SyntheticEvent, value: string) => void;
  onUrlChange?: (value: string) => void;
  sx?: SxProps<Theme>;
  tabsSx?: SxProps<Theme>;
  tabButtonSx?: SxProps<Theme>;
  contentSx?: SxProps<Theme>;
  tabsProps?: Omit<TabsProps, 'value' | 'onChange'>;
  scrollable?: boolean;
  useUrl?: boolean;
  tabParam?: string;
  basePath?: string;
  renderContent?: boolean;
}

export default function ButtonTabs({
  tabs,
  defaultValue,
  defaultTab,
  value,
  onChange,
  onUrlChange,
  sx,
  tabsSx,
  tabButtonSx,
  contentSx,
  tabsProps,
  scrollable,
  useUrl,
  tabParam = 'tab',
  basePath,
  renderContent = true,
}: ButtonTabsProps) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const shouldUseUrl = Boolean(useUrl) && tabs.length > 1;
  const initialValue = defaultValue ?? defaultTab ?? tabs[0]?.id ?? '';
  const [internalValue, setInternalValue] = useState(initialValue);

  useEffect(() => {
    if (value || shouldUseUrl) return;
    if (!tabs.length) return;
    if (internalValue && tabs.some((tab) => tab.id === internalValue)) return;
    setInternalValue(tabs[0]?.id ?? '');
  }, [tabs, internalValue, value, shouldUseUrl]);

  const fallbackValue = initialValue;
  const paramValue = searchParams.get(tabParam) ?? fallbackValue;
  const currentValue = shouldUseUrl
    ? (tabs.some((tab) => tab.id === paramValue) ? paramValue : fallbackValue)
    : (value ?? internalValue);
  const currentTab = tabs.find((tab) => tab.id === currentValue) ?? tabs[0];
  if (!currentTab) return null;

  const buildHref = (tabValue: string) => {
    const next = new URLSearchParams(searchParams);
    next.set(tabParam, tabValue);
    const path = basePath ?? location.pathname;
    const query = next.toString();
    return `${path}${query ? `?${query}` : ''}`;
  };

  const handleChange = (event: SyntheticEvent, next: string) => {
    if (shouldUseUrl) {
      onUrlChange?.(next);
      onChange?.(event, next);
      return;
    }

    if (!value) {
      setInternalValue(next);
    }
    onChange?.(event, next);
  };

  const defaultTabsSx: SxProps<Theme> = {
    gap: 1,
    mt: 3,
    '& .MuiTabs-flexContainer': {
      justifyContent: 'flex-start',
      flexWrap: 'nowrap',
      gap: 2,
    },
    '& .MuiTabs-scroller': {
      overflowX: 'auto',
    },
    '& .MuiTabs-indicator': {
      display: 'none',
    },
    '& .MuiTabs-scrollButtons.Mui-disabled': {
      opacity: 0.3,
    },
  };

  const { sx: tabsPropsSx, variant: tabsVariantProp, ...restTabsProps } = tabsProps ?? {};
  const resolvedVariant = tabsVariantProp ?? (scrollable ? 'scrollable' : 'standard');
  const isScrollable = resolvedVariant === 'scrollable';
  const assembledTabsSx = mergeSx(defaultTabsSx, tabsSx, tabsPropsSx);
  const assembledContentSx = mergeSx({ mt: 2 }, contentSx);
  const defaultButtonSx = (active: boolean): SxProps<Theme> => ({
    textTransform: 'none',
    px: 2,
    py: 0.5,
    minHeight: 40,
    borderRadius: 1,
    bgcolor: active ? 'text.main' : 'transparent',
    border: '1px solid',
    borderColor: active ? 'text.main' : 'divider',
    color: active ? 'text.primary' : 'text.secondary',
    fontWeight: active ? 600 : 500,
    '& .MuiTab-iconWrapper': {
      mr: 1,
    },
  });

  type LinkTabProps = TabProps<'a'> & { to: string };
  const LinkTab = (props: LinkTabProps) => (
    <Tab component={RouterLink} {...props} />
  );

  return (
    <Box sx={sx}>
      <Tabs
        value={currentValue}
        onChange={handleChange}
        variant={resolvedVariant}
        scrollButtons={isScrollable ? 'auto' : false}
        allowScrollButtonsMobile={isScrollable}
        sx={assembledTabsSx}
        {...restTabsProps}
      >
        {tabs.map((tab) => {
          const isActive = tab.id === currentValue;
          const { sx: tabPropsSx, icon: tabPropsIcon, ...restTabProps } = tab.tabProps ?? {};
          const buttonSx = mergeSx(defaultButtonSx(isActive), tabButtonSx, tabPropsSx);
          const icon = tab.icon ?? tabPropsIcon;
          const sharedProps = {
            value: tab.id,
            icon,
            label: tab.label,
            iconPosition: 'start' as const,
            disabled: tab.disabled,
            sx: buttonSx,
            ...restTabProps,
          };

          if (shouldUseUrl) {
            const { ref: _ref, to: _to, ...linkProps } = sharedProps as LinkTabProps;
            return (
              <LinkTab
                key={tab.id}
                to={buildHref(tab.id)}
                {...linkProps}
              />
            );
          }

          return <Tab key={tab.id} {...sharedProps} />;
        })}
      </Tabs>
      {renderContent && (
        <Box sx={assembledContentSx}>
          {typeof currentTab.content === 'function' ? currentTab.content() : currentTab.content}
        </Box>
      )}
    </Box>
  );
}
